import { useCallback, useState } from 'react';
import type { ChangeEvent } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import { EMPTY_STRING } from '@e-socna/empty';

import { Loading } from '../../components/Loading';
import { NoData } from '../../components/Illustrations/NoData';
import { SearchingEverywhere } from '../../components/Illustrations/SearchingEverywhere';
import { useDebounce } from '../../hooks/useDebounce.hook';

import { useOrganizationSearchQuery } from './hooks/useOrganizationSearchQuery.hook';
import { useCreateOrganizationFromSearchResult } from './hooks/useCreateOrganizationFromSearchResult.hook';
import { DirectorsConfirmDialog } from './DirectorsConfirmDialog';
import { EstablishmentsConfirmDialog } from './EstablishmentsConfirmDialog';
import { ResultItem } from './ResultItem';

const DEBOUNCE_DELAY_MS = 400;

const CENTERED_CONTENT_SX = {
  alignItems: 'center',
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'center',
} as const;

export const OrganizationSearchPanel = () => {
  const [query, setQuery] = useState(EMPTY_STRING);
  const debouncedQuery = useDebounce(query, DEBOUNCE_DELAY_MS);

  const handleQueryChange = useCallback(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      setQuery(event.target.value);
    },
    [],
  );

  const clearQuery = useCallback(() => {
    setQuery(EMPTY_STRING);
  }, []);

  const { data, enabled, isLoading } = useOrganizationSearchQuery({
    query: debouncedQuery,
  });

  const {
    activeMemberships,
    armedDirectors,
    armedEstablishments,
    cancelPendingOrganization,
    continueToEstablishments,
    establishmentsForDialog,
    fallbackCivilityId,
    hasPendingDirectorRows,
    hasPendingEstablishmentRows,
    isDirectorsDialogOpen,
    isEstablishmentsDialogOpen,
    isPreparingOrganization,
    pendingResult,
    physicalPersonDirectors,
    setActiveMemberships,
    setHasPendingDirectorRows,
    setHasPendingEstablishmentRows,
    startCreateOrganization,
    startDirectorsCreation,
    startEstablishmentsCreation,
  } = useCreateOrganizationFromSearchResult();

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        <TextField
          autoFocus
          fullWidth
          label="Nom, SIREN, SIRET..."
          onChange={handleQueryChange}
          placeholder="Rechercher une entreprise"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Effacer la recherche"
                    edge="end"
                    onClick={clearQuery}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
          sx={{ mb: 3 }}
          value={query}
          variant="outlined"
        />

        {!enabled ? (
          <Box sx={CENTERED_CONTENT_SX}>
            <SearchingEverywhere
              sx={{
                minHeight: 'auto',
                '& svg': { height: 'auto', width: '100%' },
              }}
            />
          </Box>
        ) : isLoading ? (
          <Loading showBackdrop={false} variant="linear" />
        ) : data?.results.length ? (
          <Grid container spacing={2}>
            {data.results.map((result) => (
              <Grid key={result.siren} size={{ xs: 12, md: 6 }}>
                <ResultItem
                  isCreateOrganizationDisabled={isPreparingOrganization}
                  onCreateOrganization={startCreateOrganization}
                  result={result}
                />
              </Grid>
            ))}
          </Grid>
        ) : data ? (
          <Box sx={CENTERED_CONTENT_SX}>
            <NoData />
          </Box>
        ) : null}
      </Box>

      <DirectorsConfirmDialog
        armedDirectors={armedDirectors}
        directors={physicalPersonDirectors}
        fallbackCivilityId={fallbackCivilityId}
        hasPendingRows={hasPendingDirectorRows}
        isOpen={isDirectorsDialogOpen}
        onActiveMembershipsChange={setActiveMemberships}
        onCancel={cancelPendingOrganization}
        onContinue={continueToEstablishments}
        onPendingChange={setHasPendingDirectorRows}
        onStartCreation={startDirectorsCreation}
      />

      <EstablishmentsConfirmDialog
        activeMemberships={activeMemberships}
        armedEstablishments={armedEstablishments}
        establishments={establishmentsForDialog}
        hasPendingRows={hasPendingEstablishmentRows}
        isOpen={isEstablishmentsDialogOpen}
        onCancel={cancelPendingOrganization}
        onPendingChange={setHasPendingEstablishmentRows}
        onStartCreation={startEstablishmentsCreation}
        result={pendingResult}
      />
    </>
  );
};
